import { config } from "../config";

export type WidgetSseMessage = { data: string };

export interface WidgetSseConnection {
  close: () => void;
}

export interface OpenWidgetSseOptions {
  url: string;
  onOpen?: () => void;
  onMessage?: (event: WidgetSseMessage) => void;
  onError?: (error: unknown) => void;
  /** Extra request headers (e.g. `x-link-id`). Merged after defaults. */
  headers?: Record<string, string>;
  /** Called on every connect/reconnect so Clerk tokens can refresh. */
  getHeaders?: () => Promise<Record<string, string>>;
  /** Delay before reconnecting after a drop (ms). Default 3000. */
  reconnectDelayMs?: number;
}

/**
 * EventSource alternative based on fetch so we can send Authorization and
 * `x-link-id`. Native EventSource cannot set those headers.
 */
export function openWidgetSse({
  url,
  onOpen,
  onMessage,
  onError,
  headers: extraHeaders,
  getHeaders,
  reconnectDelayMs = 3000,
}: OpenWidgetSseOptions): WidgetSseConnection {
  let closed = false;
  let controller: AbortController | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  const clearReconnect = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const scheduleReconnect = () => {
    if (closed) return;
    clearReconnect();
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      void connect();
    }, reconnectDelayMs);
  };

  const connect = async () => {
    if (closed) return;

    controller?.abort();
    controller = new AbortController();

    const headers: Record<string, string> = {
      Accept: "text/event-stream",
      ...extraHeaders,
    };
    if (config.API_BASE_URL.includes("ngrok")) {
      headers["ngrok-skip-browser-warning"] = "true";
    }
    const dynamic = (await getHeaders?.()) ?? {};
    Object.assign(headers, dynamic);

    try {
      const response = await fetch(url, {
        method: "GET",
        headers,
        signal: controller.signal,
        cache: "no-store",
      });

      if (!response.ok || !response.body) {
        throw new Error(`SSE HTTP ${response.status}`);
      }

      onOpen?.();

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (!closed) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        buffer = emitSseFrames(buffer, onMessage);
      }

      if (!closed) {
        onError?.(new Error("SSE stream ended"));
        scheduleReconnect();
      }
    } catch (error) {
      if (closed) return;
      if (error instanceof DOMException && error.name === "AbortError") return;
      onError?.(error);
      scheduleReconnect();
    }
  };

  void connect();

  return {
    close: () => {
      closed = true;
      clearReconnect();
      controller?.abort();
      controller = null;
    },
  };
}

function emitSseFrames(
  buffer: string,
  onMessage?: (event: WidgetSseMessage) => void
): string {
  const parts = buffer.split("\n\n");
  const rest = parts.pop() ?? "";

  for (const part of parts) {
    if (!part.trim() || part.startsWith(":")) continue;

    const dataLines: string[] = [];
    for (const line of part.split("\n")) {
      if (line.startsWith("data:")) {
        dataLines.push(line.slice(5).trimStart());
      }
    }
    if (dataLines.length === 0) continue;
    onMessage?.({ data: dataLines.join("\n") });
  }

  return rest;
}
